import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Shuffle, Play, RefreshCw, ChevronDown, AlertTriangle } from "lucide-react";
import { t, useLang } from "../../lib/i18n";
import { playerInitial } from "../../lib/playerColors";
import { TurnRing } from "./TurnRing";

/**
 * The redeal vote, at the start of each round (phase "redeal_vote"). Players vote one by
 * one, counter-clockwise: "Rejouer" passes to the next one (all of them: new cards),
 * "Continuer" ends the vote and the round starts.
 *
 * - A player in the vote: `RedealVoteSheet`, docked right above the hand, while Game.jsx
 *   dims the table and lights the hand (the owner wanted the player to look at the cards
 *   first). It stays until somebody says « Continuer » (owner's request): the buttons on my
 *   turn, who votes now the rest of the time. Said by somebody else, the sheet stays with who
 *   chose and an OK (`endedBy`, owner's request); my own « Continuer » ends it at once.
 * - A player out of it (eliminated): `RedealVoteCapsule`, a small plate at the top of the table.
 *
 * Styled like a card game's mulligan screen (owner's request: "style pro video games"): a
 * gold-framed plate, a ribbon, flat buttons (`.rv-*` in index.css). Still, with no loop
 * (owner: "trop animé"): only a short entrance, and the hand's wave in PlayerHand. No bold
 * text (the app's rule): the weight comes from gradients, glows and sizes.
 */

// Who voted (« Rejouer »), who votes now, who is still to vote: in the order they vote.
// Once the vote is over (`endedBy`), the server's queue is empty: the order is the last one
// seen during the vote, with who said « Continuer », and who never got to vote dimmed.
function voters(game, endedBy, order) {
  if (endedBy) {
    const rejouer = new Set(game.redeal_voted_rejouer || []);
    const byId = Object.fromEntries(game.players.map((p) => [p.user_id, p]));
    const ids = order.includes(endedBy) ? order : [...rejouer, endedBy];
    return ids.map((id) => byId[id]).filter((p) => p && !p.eliminated).map((p) => ({
      p,
      state: p.user_id === endedBy ? "continuer" : rejouer.has(p.user_id) ? "rejouer" : "waiting",
    }));
  }
  const queue = game.redeal_vote_queue || [];
  const rejouer = new Set(game.redeal_voted_rejouer || []);
  const byId = Object.fromEntries(game.players.map((p) => [p.user_id, p]));
  const done = game.players.filter((p) => !p.eliminated && rejouer.has(p.user_id));
  const waiting = queue.map((id) => byId[id]).filter(Boolean);
  return [
    ...done.map((p) => ({ p, state: "rejouer" })),
    ...waiting.map((p, i) => ({ p, state: i === 0 ? "current" : "waiting" })),
  ];
}

// Medallions: a gold rim (the player's colour inside), ↺ once they said « Rejouer », a bright
// gold ring on the one voting now, and on the one who said « Continuer » (▶). The one voting now also
// wears their vote's time (TurnRing): the scrim hides their pill's.
function Voters({ game, me, playerColorMap, size = 22, endedBy = null, order = [], clock = null }) {
  return (
    <div className="flex items-center justify-center gap-1.5" dir="ltr">
      {voters(game, endedBy, order).map(({ p, state }) => {
        const color = playerColorMap?.[p.user_id];
        const isMe = p.user_id === me?.user_id;
        return (
          <div
            key={p.user_id}
            title={isMe ? t("common.you") : p.name}
            className={`rv-medal relative ${state === "current" || state === "continuer" ? "rv-medal-now" : state === "waiting" ? "opacity-45" : ""}`}
            style={{ width: size, height: size }}
          >
            <span
              className="absolute inset-[1px] rounded-full flex items-center justify-center text-[8.5px] tabular-nums"
              style={{ color: color?.hex ?? "#f1f5f9", background: "radial-gradient(circle at 35% 30%, #1e293b, #020617)" }}
            >
              {playerInitial(p)}
            </span>
            {state === "current" && clock?.userId === p.user_id && <TurnRing clock={clock} size={size} gap={1} stroke={2} />}
            {state === "rejouer" && (
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-gradient-to-b from-emerald-300 to-emerald-600 text-emerald-950 flex items-center justify-center shadow">
                <RefreshCw className="w-[8px] h-[8px]" strokeWidth={3} />
              </span>
            )}
            {state === "continuer" && (
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-gradient-to-b from-amber-200 to-amber-500 text-amber-950 flex items-center justify-center shadow">
                <Play className="w-[7px] h-[7px] fill-current" strokeWidth={3} />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function RedealVoteSheet({ game, me, playerColorMap, onVote, loading = false, myTurn = true, handSize, endedBy = null, onOk, clock = null }) {
  useLang();
  const penalty = game.continuer_penalty ?? 50;
  const vote = (choice) => !loading && onVote(choice);
  const current = game.players.find((p) => p.user_id === game.redeal_vote_current);
  const iSaidRejouer = (game.redeal_voted_rejouer || []).includes(me?.user_id);
  const continuer = endedBy && game.players.find((p) => p.user_id === endedBy);
  // The voting order, kept from the vote for the result (the server empties its queue).
  const orderRef = useRef([]);
  if (!endedBy) orderRef.current = voters(game).map(({ p }) => p.user_id);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="rv-frame relative w-full max-w-[300px] mt-3"
      role="group"
      aria-label={t("redeal.question")}
      data-testid="redeal-vote-sheet"
    >
      <div className="rv-plate relative overflow-hidden rounded-[17px] px-3 pt-5 pb-3 text-center">
        <div className="relative">
          {/* Over: who chose takes the question's place (owner's request). */}
          {continuer ? (
            <h2 className="flex items-center justify-center gap-1.5" data-testid="redeal-vote-result">
              <Play className="w-3 h-3 fill-current text-amber-300 shrink-0" />
              <span className="rv-title text-[15px] leading-tight">{t("redeal.chose", { name: continuer.name })}</span>
            </h2>
          ) : (
            <>
              <h2 className="rv-title text-[16px] leading-tight">{t("redeal.question")}</h2>
              <p className="text-[11px] text-amber-50/75 mt-0.5 flex items-center justify-center gap-1">
                {t("redeal.lookHand", { n: handSize })}
                <ChevronDown className="w-3 h-3 text-amber-300 shrink-0" />
              </p>
            </>
          )}

          <div className="mt-2">
            <Voters game={game} me={me} playerColorMap={playerColorMap} endedBy={endedBy} order={orderRef.current} clock={clock} />
          </div>

          {/* Over: the buttons give way to an OK, which closes the vote for me. */}
          {endedBy && (
            <button type="button" onClick={onOk} className="rv-btn rv-btn-gold w-full mt-2.5" data-testid="redeal-ok">
              <span className="rv-btn-face">
                <span className="rv-btn-label">{t("redeal.ok")}</span>
              </span>
            </button>
          )}

          {/* Not my turn: who votes now, in the buttons' room (the sheet keeps its size). */}
          {!endedBy && !myTurn && (
            <div className="rv-wait mt-2.5 h-[42px] rounded-xl flex flex-col items-center justify-center gap-0.5" data-testid="redeal-vote-waiting">
              <span className="text-[12px] text-slate-100">
                {current ? t("redeal.thinking", { name: current.name }) : "…"}
              </span>
              <span className={`text-[9.5px] ${iSaidRejouer ? "text-emerald-300/90" : "text-amber-200/70"}`}>
                {iSaidRejouer ? t("redeal.youVoted") : t("redeal.turnSoon")}
              </span>
            </div>
          )}

          {!endedBy && myTurn && <div className="grid grid-cols-2 gap-2 mt-2.5">
            <button
              type="button"
              onClick={() => vote("rejouer")}
              disabled={loading}
              className="rv-btn rv-btn-blue"
              data-testid="redeal-rejouer"
            >
              <span className="rv-btn-face">
                <span className="rv-btn-label">
                  <Shuffle className="w-3.5 h-3.5" />
                  {t("redeal.rejouer")}
                </span>
                <span className="rv-btn-sub">{t("redeal.rejouerHint")}</span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => vote("continuer")}
              disabled={loading}
              className="rv-btn rv-btn-gold"
              data-testid="redeal-continuer"
            >
              <span className="rv-btn-face">
                <span className="rv-btn-label">
                  <Play className="w-3.5 h-3.5 fill-current" />
                  {t("redeal.continuer")}
                </span>
                <span className="rv-btn-sub">{t("redeal.continuerHint")}</span>
              </span>
            </button>
          </div>}

          {!endedBy && penalty > 0 && (
            <div className="rv-stake mt-2 mx-auto w-fit flex items-center gap-1 ps-1.5 pe-2 py-0.5 rounded-full text-[9.5px] text-amber-100/90">
              <AlertTriangle className="w-2.5 h-2.5 text-amber-400 shrink-0" />
              {t("redeal.stake", { n: penalty })}
            </div>
          )}
        </div>
      </div>

      <div className="rv-ribbon absolute -top-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1 px-4 py-0.5 text-[9px] whitespace-nowrap">
        <Shuffle className="w-3 h-3" />
        <span className="rv-ribbon-text">{t("redeal.title")}</span>
      </div>
    </motion.div>
  );
}

export function RedealVoteCapsule({ game, me, playerColorMap }) {
  useLang();
  const current = game.players.find((p) => p.user_id === game.redeal_vote_current);
  const iSaidRejouer = (game.redeal_voted_rejouer || []).includes(me?.user_id);
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="rv-capsule rounded-full"
      data-testid="redeal-vote-capsule"
    >
      <div className="rv-capsule-inner rounded-full flex items-center gap-2.5 ps-1.5 pe-3 py-1.5">
        <span className="rv-medal w-8 h-8 shrink-0 relative">
          <span className="absolute inset-[1px] rounded-full flex items-center justify-center bg-slate-950">
            <Shuffle className="w-3.5 h-3.5 text-amber-300" />
          </span>
        </span>
        <div className="leading-tight text-start">
          <div className="rv-capsule-kicker text-[9px] uppercase text-amber-300/85">
            {iSaidRejouer ? t("redeal.youVoted") : t("redeal.title")}
          </div>
          <div className="text-[11.5px] text-slate-100 max-w-[150px] truncate">
            {current ? t("redeal.thinking", { name: current.name }) : "…"}
          </div>
        </div>
        <Voters game={game} me={me} playerColorMap={playerColorMap} size={22} />
      </div>
    </motion.div>
  );
}

export default RedealVoteSheet;
